import { API_URL } from '@/config/api';
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Activity, 
  Database, 
  Shield, 
  Key, 
  Copy, 
  Download, 
  Play, 
  RefreshCw, 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  Eye, 
  EyeOff, 
  Clock,
  Trash2,
  Server
} from 'lucide-react';
import axios from 'axios';
import AdminNav from '../components/AdminNav';
import AdminTopMenuBar from '../components/AdminTopMenuBar';
import AdminMobileBottomNav from '../components/AdminMobileBottomNav';

const API = API_URL;

/**
 * Admin Diagnostics Page
 * 
 * Provides UI access to production diagnostic endpoints:
 * - GET /api/admin/diagnostics/dataplane
 * - GET /api/admin/diagnostics/auth-consistency?email=...
 * - POST /api/admin/diagnostics/auth-consistency { email, password }
 * 
 * Admin-only. Does not modify any data except for the POST endpoint which
 * verifies a password (read-only verification).
 */

// Status badge component
const StatusBadge = ({ status }) => {
  const styles = {
    IDLE: 'bg-slate-700 text-slate-300',
    RUNNING: 'bg-blue-600 text-white animate-pulse',
    DONE: 'bg-green-600 text-white',
    ERROR: 'bg-red-600 text-white'
  };
  
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${styles[status] || styles.IDLE}`}>
      {status}
    </span>
  );
};

// Key signal display component
const KeySignal = ({ label, value, isOk = true, isWarning = false }) => {
  let colorClass = 'text-slate-300';
  if (!isOk) colorClass = 'text-red-400';
  else if (isWarning) colorClass = 'text-yellow-400';
  else if (isOk && value !== undefined) colorClass = 'text-green-400';
  
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-800/50 rounded-lg">
      <span className="text-xs text-slate-400">{label}:</span>
      <span className={`text-sm font-mono font-semibold ${colorClass}`}>
        {String(value ?? 'N/A')}
      </span>
    </div>
  );
};

// Warning banner component
const WarningBanner = ({ warnings }) => {
  if (!warnings || warnings.length === 0) return null;
  
  return (
    <div className="mb-4 p-3 bg-red-500/10 border border-red-400/30 rounded-lg">
      <div className="flex items-center gap-2 text-red-400 font-semibold mb-2">
        <AlertTriangle className="w-4 h-4" />
        <span>Warnings Detected</span>
      </div>
      <ul className="text-sm text-red-300 space-y-1">
        {warnings.map((warning, idx) => (
          <li key={idx} className="flex items-start gap-2">
            <span className="text-red-400">•</span>
            <span>{warning}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

// JSON Viewer component with copy/download
const JSONViewer = ({ data, filename, duration, timestamp }) => {
  const [copied, setCopied] = useState(false);
  
  const jsonString = JSON.stringify(data, null, 2);
  
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(jsonString);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  const handleDownload = () => {
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  
  return (
    <div className="mt-4">
      {/* Metadata row */}
      {(timestamp || duration) && (
        <div className="flex items-center gap-4 mb-2 text-xs text-slate-400">
          {timestamp && (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {new Date(timestamp).toLocaleString()}
            </span>
          )}
          {duration && (
            <span>Duration: {duration}ms</span>
          )}
        </div>
      )}
      
      {/* Actions */}
      <div className="flex items-center gap-2 mb-2">
        <Button
          size="sm"
          variant="outline"
          onClick={handleCopy}
          className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700 text-xs"
        >
          <Copy className="w-3 h-3 mr-1" />
          {copied ? 'Copied!' : 'Copy JSON'}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={handleDownload}
          className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700 text-xs"
        >
          <Download className="w-3 h-3 mr-1" />
          Download JSON
        </Button>
      </div>
      
      {/* JSON display */}
      <pre className="p-4 bg-slate-950 rounded-lg border border-slate-800 overflow-x-auto text-xs text-slate-300 font-mono max-h-[400px] overflow-y-auto">
        {jsonString}
      </pre>
    </div>
  );
};

const AdminDiagnosticsPage = () => {
  const { user } = useAuth();
  
  // Global status
  const [globalStatus, setGlobalStatus] = useState('IDLE');
  
  // Panel 1: Data Plane
  const [dataPlaneStatus, setDataPlaneStatus] = useState('IDLE');
  const [dataPlaneResult, setDataPlaneResult] = useState(null);
  const [dataPlaneError, setDataPlaneError] = useState(null);
  const [dataPlaneTimestamp, setDataPlaneTimestamp] = useState(null);
  const [dataPlaneDuration, setDataPlaneDuration] = useState(null);
  const [autoRunDataPlane, setAutoRunDataPlane] = useState(true);
  
  // Panel 2: Auth Consistency (GET)
  const [authGetStatus, setAuthGetStatus] = useState('IDLE');
  const [authGetResult, setAuthGetResult] = useState(null);
  const [authGetError, setAuthGetError] = useState(null);
  const [authGetEmail, setAuthGetEmail] = useState('');
  const [authGetTimestamp, setAuthGetTimestamp] = useState(null);
  const [authGetDuration, setAuthGetDuration] = useState(null);
  
  // Panel 3: Auth Verification (POST)
  const [authPostStatus, setAuthPostStatus] = useState('IDLE');
  const [authPostResult, setAuthPostResult] = useState(null);
  const [authPostError, setAuthPostError] = useState(null);
  const [authPostEmail, setAuthPostEmail] = useState('');
  const [authPostPassword, setAuthPostPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [authPostTimestamp, setAuthPostTimestamp] = useState(null);
  const [authPostDuration, setAuthPostDuration] = useState(null);
  
  // Panel 4: Database Reset
  const [resetStatus, setResetStatus] = useState('IDLE');
  const [resetResult, setResetResult] = useState(null);
  const [resetError, setResetError] = useState(null);
  const [resetConfirmation, setResetConfirmation] = useState('');
  const [resetTimestamp, setResetTimestamp] = useState(null);
  const [resetDuration, setResetDuration] = useState(null);
  
  // Get auth token
  const getToken = () => localStorage.getItem('accessToken');
  
  // ==========================================================================
  // Panel 1: Data Plane Check
  // ==========================================================================
  const runDataPlaneCheck = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setDataPlaneError('No authentication token found');
      setDataPlaneStatus('ERROR');
      return;
    }
    
    setDataPlaneStatus('RUNNING');
    setDataPlaneError(null);
    const startTime = Date.now();
    
    try {
      const response = await axios.get(`${API}/api/admin/diagnostics/dataplane`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setDataPlaneResult(response.data);
      setDataPlaneTimestamp(new Date().toISOString());
      setDataPlaneDuration(Date.now() - startTime);
      setDataPlaneStatus('DONE');
    } catch (error) {
      console.error('Data plane check error:', error);
      setDataPlaneError(error.response?.data || error.message);
      setDataPlaneStatus('ERROR');
      setDataPlaneDuration(Date.now() - startTime);
    }
  }, []);
  
  // Auto-run data plane on mount
  useEffect(() => {
    if (autoRunDataPlane) {
      // Using a timeout to avoid calling setState directly in effect
      const timer = setTimeout(() => {
        runDataPlaneCheck();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [autoRunDataPlane, runDataPlaneCheck]);
  
  // ==========================================================================
  // Panel 2: Auth Consistency Check (GET)
  // ==========================================================================
  const runAuthConsistencyCheck = async () => {
    if (!authGetEmail.trim()) {
      setAuthGetError('Email is required');
      return;
    }
    
    const token = getToken();
    if (!token) {
      setAuthGetError('No authentication token found');
      setAuthGetStatus('ERROR');
      return;
    }
    
    setAuthGetStatus('RUNNING');
    setAuthGetError(null);
    const startTime = Date.now();
    
    try {
      const response = await axios.get(`${API}/api/admin/diagnostics/auth-consistency`, {
        params: { email: authGetEmail.trim() },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAuthGetResult(response.data);
      setAuthGetTimestamp(new Date().toISOString());
      setAuthGetDuration(Date.now() - startTime);
      setAuthGetStatus('DONE');
    } catch (error) {
      console.error('Auth consistency check error:', error);
      setAuthGetError(error.response?.data || error.message);
      setAuthGetStatus('ERROR');
      setAuthGetDuration(Date.now() - startTime);
    }
  };
  
  // ==========================================================================
  // Panel 3: Auth Verification (POST)
  // ==========================================================================
  const runAuthVerification = async () => {
    if (!authPostEmail.trim()) {
      setAuthPostError('Email is required');
      return;
    }
    if (!authPostPassword) {
      setAuthPostError('Password is required');
      return;
    }
    
    const token = getToken();
    if (!token) {
      setAuthPostError('No authentication token found');
      setAuthPostStatus('ERROR');
      return;
    }
    
    setAuthPostStatus('RUNNING');
    setAuthPostError(null);
    const startTime = Date.now();
    
    try {
      const response = await axios.post(
        `${API}/api/admin/diagnostics/auth-consistency`,
        { email: authPostEmail.trim(), password: authPostPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAuthPostResult(response.data);
      setAuthPostTimestamp(new Date().toISOString());
      setAuthPostDuration(Date.now() - startTime);
      setAuthPostStatus('DONE');
      
      // Clear password on success
      setAuthPostPassword('');
      setShowPassword(false);
    } catch (error) {
      console.error('Auth verification error:', error);
      setAuthPostError(error.response?.data || error.message);
      setAuthPostStatus('ERROR');
      setAuthPostDuration(Date.now() - startTime);
    }
  };
  
  // ==========================================================================
  // Panel 4: Database Reset
  // ==========================================================================
  const runDatabaseReset = async () => {
    if (resetConfirmation !== 'RESET_ALL_DATA') {
      setResetError('Confirmation phrase does not match');
      return;
    }
    
    const token = getToken();
    if (!token) {
      setResetError('No authentication token found');
      setResetStatus('ERROR');
      return;
    }
    
    // Double-confirm with browser dialog
    const confirmed = window.confirm(
      '⚠️ FINAL WARNING ⚠️\n\n' +
      'You are about to DELETE ALL DATA in the database.\n\n' +
      'This action CANNOT be undone.\n\n' +
      'Only the admin seed accounts will remain.\n\n' +
      'Are you absolutely sure?'
    );
    
    if (!confirmed) {
      return;
    }
    
    setResetStatus('RUNNING');
    setResetError(null);
    const startTime = Date.now();
    
    try {
      const response = await axios.post(
        `${API}/api/admin/diagnostics/reset-database`,
        { confirm: 'RESET_ALL_DATA' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setResetResult(response.data);
      setResetTimestamp(new Date().toISOString());
      setResetDuration(Date.now() - startTime);
      setResetStatus('DONE');
      
      // Clear confirmation
      setResetConfirmation('');
      
      // Auto-refresh data plane to show new state
      setTimeout(() => {
        runDataPlaneCheck();
      }, 1000);
      
    } catch (error) {
      console.error('Database reset error:', error);
      setResetError(error.response?.data || error.message);
      setResetStatus('ERROR');
      setResetDuration(Date.now() - startTime);
    }
  };
  
  // ==========================================================================
  // Run All Diagnostics
  // ==========================================================================
  const runAllDiagnostics = async () => {
    setGlobalStatus('RUNNING');
    
    // Always run data plane
    await runDataPlaneCheck();
    
    // Run auth consistency if email provided
    if (authGetEmail.trim()) {
      await runAuthConsistencyCheck();
    }
    
    // Run auth verification if email and password provided
    if (authPostEmail.trim() && authPostPassword) {
      await runAuthVerification();
    }
    
    setGlobalStatus('DONE');
  };
  
  // ==========================================================================
  // Clear All Results
  // ==========================================================================
  const clearAllResults = () => {
    setGlobalStatus('IDLE');
    
    setDataPlaneStatus('IDLE');
    setDataPlaneResult(null);
    setDataPlaneError(null);
    setDataPlaneTimestamp(null);
    setDataPlaneDuration(null);
    
    setAuthGetStatus('IDLE');
    setAuthGetResult(null);
    setAuthGetError(null);
    setAuthGetTimestamp(null);
    setAuthGetDuration(null);
    
    setAuthPostStatus('IDLE');
    setAuthPostResult(null);
    setAuthPostError(null);
    setAuthPostTimestamp(null);
    setAuthPostDuration(null);
    setAuthPostPassword('');
    setShowPassword(false);
    
    setResetStatus('IDLE');
    setResetResult(null);
    setResetError(null);
    setResetConfirmation('');
    setResetTimestamp(null);
    setResetDuration(null);
  };
  
  // ==========================================================================
  // Warning Detection
  // ==========================================================================
  const getDataPlaneWarnings = () => {
    if (!dataPlaneResult) return [];
    const warnings = [];
    
    // Only warn if NOT production AND on remote cluster
    if (dataPlaneResult.env !== 'production' && dataPlaneResult.is_localhost === false) {
      warnings.push(`Environment is '${dataPlaneResult.env}' (should be production for remote cluster)`);
    }
    // Don't warn about db_name - Emergent assigns it
    if (dataPlaneResult.is_localhost === true && dataPlaneResult.env === 'production') {
      warnings.push('MongoDB is connected to localhost in production (not allowed)');
    }
    if (dataPlaneResult.mongo_can_write === false) {
      warnings.push('MongoDB write test FAILED - database may be read-only');
    }
    
    // Check for empty users (at least 1 seeded user expected)
    const counts = dataPlaneResult.counts || {};
    if (typeof counts.users === 'number' && counts.users === 0) {
      warnings.push('Users collection is EMPTY - run seeding');
    }
    
    return warnings;
  };
  
  const getAuthGetWarnings = () => {
    if (!authGetResult) return [];
    const warnings = [];
    
    if (authGetResult.found === false) {
      warnings.push('User NOT FOUND in database');
    }
    if (authGetResult.auth_provider !== 'local') {
      warnings.push(`auth_provider is '${authGetResult.auth_provider}' (expected: local for password auth)`);
    }
    if (authGetResult.can_local_auth === false) {
      warnings.push('User CANNOT authenticate locally - check auth_provider and password_hash');
    }
    
    return warnings;
  };
  
  const getAuthPostWarnings = () => {
    if (!authPostResult) return [];
    const warnings = [];
    
    if (authPostResult.can_authenticate === false) {
      warnings.push(`Authentication FAILED: ${authPostResult.reason}`);
    }
    
    return warnings;
  };
  
  // ==========================================================================
  // RENDER
  // ==========================================================================
  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      {/* Admin Navigation */}
      <AdminNav />
      <AdminTopMenuBar />
      
      {/* Main Content */}
      <div className="pb-20 md:pb-0">
        {/* Header */}
        <header className="bg-gradient-to-br from-amber-900/30 via-slate-800 to-slate-900 border-b border-slate-800">
          <div className="mx-auto max-w-7xl px-4 py-8 sm:py-10">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <Server className="w-8 h-8 text-amber-400" />
                <h1 className="text-2xl sm:text-3xl font-bold">Production Diagnostics</h1>
              </div>
              <StatusBadge status={globalStatus} />
            </div>
            <p className="text-sm text-slate-400 max-w-3xl">
              Evidence-only reports from the live environment. This page does not change data unless you explicitly run the password verification check.
            </p>
            
            {/* Header Actions */}
            <div className="flex items-center gap-3 mt-6">
              <Button
                onClick={runAllDiagnostics}
                disabled={globalStatus === 'RUNNING'}
                className="bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
                data-testid="run-all-diagnostics-btn"
              >
                <Play className="w-4 h-4 mr-2" />
                {globalStatus === 'RUNNING' ? 'Running...' : 'Run All Diagnostics'}
              </Button>
              <Button
                onClick={clearAllResults}
                variant="outline"
                className="bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700"
                data-testid="clear-results-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear Results
              </Button>
            </div>
          </div>
        </header>

        {/* Main Content - Three Panels */}
        <main className="mx-auto max-w-7xl px-4 py-8 space-y-8">
          
          {/* =================================================================== */}
          {/* Panel 1: Data Plane Integrity */}
          {/* =================================================================== */}
          <section className="rounded-2xl bg-slate-900/60 shadow-xl ring-1 ring-slate-800 overflow-hidden">
            <div className="p-6 border-b border-slate-800">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Database className="w-6 h-6 text-cyan-400" />
                  <h2 className="text-lg font-bold">1) Data Plane Integrity</h2>
                  <code className="text-xs bg-slate-800 px-2 py-1 rounded text-slate-400">
                    GET /api/admin/diagnostics/dataplane
                  </code>
                </div>
                <StatusBadge status={dataPlaneStatus} />
              </div>
              
              <div className="flex items-center gap-4">
                <Button
                  onClick={runDataPlaneCheck}
                  disabled={dataPlaneStatus === 'RUNNING'}
                  className="bg-cyan-600 text-white hover:bg-cyan-700 disabled:opacity-50"
                  data-testid="run-dataplane-check-btn"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${dataPlaneStatus === 'RUNNING' ? 'animate-spin' : ''}`} />
                  Run Data Plane Check
                </Button>
                
                <label className="flex items-center gap-2 text-sm text-slate-400 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoRunDataPlane}
                    onChange={(e) => setAutoRunDataPlane(e.target.checked)}
                    className="rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500"
                  />
                  Auto-run on page load
                </label>
              </div>
            </div>
            
            <div className="p-6">
              {/* Warnings */}
              <WarningBanner warnings={getDataPlaneWarnings()} />
              
              {/* Key Signals */}
              {dataPlaneResult && (
                <div className="mb-4">
                  <h4 className="text-xs text-slate-400 uppercase tracking-wider mb-2">Key Signals</h4>
                  <div className="flex flex-wrap gap-2">
                    <KeySignal 
                      label="env" 
                      value={dataPlaneResult.env} 
                      isOk={dataPlaneResult.env === 'production'}
                      isWarning={dataPlaneResult.env !== 'production'}
                    />
                    <KeySignal 
                      label="db_name" 
                      value={dataPlaneResult.db_name}
                      isOk={!!dataPlaneResult.db_name}
                    />
                    <KeySignal 
                      label="mongo_cluster_fingerprint" 
                      value={dataPlaneResult.mongo_cluster_fingerprint}
                    />
                    <KeySignal 
                      label="can_write" 
                      value={dataPlaneResult.mongo_can_write}
                      isOk={dataPlaneResult.mongo_can_write === true}
                    />
                    <KeySignal 
                      label="is_localhost" 
                      value={dataPlaneResult.is_localhost}
                      isOk={dataPlaneResult.is_localhost === false}
                    />
                    {dataPlaneResult.counts && (
                      <>
                        <KeySignal 
                          label="users" 
                          value={dataPlaneResult.counts.users}
                          isOk={dataPlaneResult.counts.users > 0}
                        />
                        <KeySignal 
                          label="manuscripts" 
                          value={dataPlaneResult.counts.manuscripts}
                          isOk={dataPlaneResult.counts.manuscripts >= 0}
                        />
                      </>
                    )}
                  </div>
                </div>
              )}
              
              {/* Error display */}
              {dataPlaneError && (
                <div className="p-4 bg-red-500/10 border border-red-400/30 rounded-lg mb-4">
                  <div className="flex items-center gap-2 text-red-400 font-semibold mb-2">
                    <XCircle className="w-4 h-4" />
                    <span>Error</span>
                  </div>
                  <pre className="text-xs text-red-300 overflow-x-auto">
                    {typeof dataPlaneError === 'string' ? dataPlaneError : JSON.stringify(dataPlaneError, null, 2)}
                  </pre>
                </div>
              )}
              
              {/* JSON Result */}
              {dataPlaneResult && (
                <JSONViewer
                  data={dataPlaneResult}
                  filename={`dataplane_${new Date().toISOString().replace(/[:.]/g, '-')}.json`}
                  timestamp={dataPlaneTimestamp}
                  duration={dataPlaneDuration}
                />
              )}
              
              {/* Empty state */}
              {!dataPlaneResult && !dataPlaneError && dataPlaneStatus === 'IDLE' && (
                <p className="text-sm text-slate-500 italic">Click &quot;Run Data Plane Check&quot; to retrieve diagnostics.</p>
              )}
            </div>
          </section>
          
          {/* =================================================================== */}
          {/* Panel 2: Auth Consistency (GET) */}
          {/* =================================================================== */}
          <section className="rounded-2xl bg-slate-900/60 shadow-xl ring-1 ring-slate-800 overflow-hidden">
            <div className="p-6 border-b border-slate-800">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Shield className="w-6 h-6 text-purple-400" />
                  <h2 className="text-lg font-bold">2) Auth Consistency — User Record</h2>
                  <code className="text-xs bg-slate-800 px-2 py-1 rounded text-slate-400">
                    GET /api/admin/diagnostics/auth-consistency
                  </code>
                </div>
                <StatusBadge status={authGetStatus} />
              </div>
              
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex-1 min-w-[250px]">
                  <Input
                    type="email"
                    placeholder="Enter user email..."
                    value={authGetEmail}
                    onChange={(e) => setAuthGetEmail(e.target.value)}
                    className="bg-slate-950/50 border-slate-700 text-slate-100"
                    data-testid="auth-get-email-input"
                  />
                </div>
                <Button
                  onClick={runAuthConsistencyCheck}
                  disabled={authGetStatus === 'RUNNING' || !authGetEmail.trim()}
                  className="bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
                  data-testid="run-auth-get-btn"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${authGetStatus === 'RUNNING' ? 'animate-spin' : ''}`} />
                  Run Auth Consistency Check
                </Button>
              </div>
            </div>
            
            <div className="p-6">
              {/* Warnings */}
              <WarningBanner warnings={getAuthGetWarnings()} />
              
              {/* Key Signals */}
              {authGetResult && (
                <div className="mb-4">
                  <h4 className="text-xs text-slate-400 uppercase tracking-wider mb-2">Key Signals</h4>
                  <div className="flex flex-wrap gap-2">
                    <KeySignal 
                      label="found" 
                      value={authGetResult.found}
                      isOk={authGetResult.found === true}
                    />
                    <KeySignal 
                      label="auth_provider" 
                      value={authGetResult.auth_provider}
                      isOk={authGetResult.auth_provider === 'local'}
                    />
                    <KeySignal 
                      label="can_local_auth" 
                      value={authGetResult.can_local_auth}
                      isOk={authGetResult.can_local_auth === true}
                    />
                    {authGetResult.login_credential_field_present !== undefined && (
                      <KeySignal 
                        label="has_password_hash" 
                        value={authGetResult.login_credential_field_present}
                        isOk={authGetResult.login_credential_field_present === true}
                      />
                    )}
                  </div>
                </div>
              )}
              
              {/* Error display */}
              {authGetError && (
                <div className="p-4 bg-red-500/10 border border-red-400/30 rounded-lg mb-4">
                  <div className="flex items-center gap-2 text-red-400 font-semibold mb-2">
                    <XCircle className="w-4 h-4" />
                    <span>Error</span>
                  </div>
                  <pre className="text-xs text-red-300 overflow-x-auto">
                    {typeof authGetError === 'string' ? authGetError : JSON.stringify(authGetError, null, 2)}
                  </pre>
                </div>
              )}
              
              {/* JSON Result */}
              {authGetResult && (
                <JSONViewer
                  data={authGetResult}
                  filename={`auth_consistency_get_${authGetEmail.replace('@', '_at_')}_${new Date().toISOString().replace(/[:.]/g, '-')}.json`}
                  timestamp={authGetTimestamp}
                  duration={authGetDuration}
                />
              )}
              
              {/* Empty state */}
              {!authGetResult && !authGetError && authGetStatus === 'IDLE' && (
                <p className="text-sm text-slate-500 italic">Enter an email and click &quot;Run Auth Consistency Check&quot;.</p>
              )}
            </div>
          </section>
          
          {/* =================================================================== */}
          {/* Panel 3: Auth Verification (POST) */}
          {/* =================================================================== */}
          <section className="rounded-2xl bg-slate-900/60 shadow-xl ring-1 ring-slate-800 overflow-hidden">
            <div className="p-6 border-b border-slate-800">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Key className="w-6 h-6 text-amber-400" />
                  <h2 className="text-lg font-bold">3) Auth Verification — Password Check</h2>
                  <code className="text-xs bg-slate-800 px-2 py-1 rounded text-slate-400">
                    POST /api/admin/diagnostics/auth-consistency
                  </code>
                </div>
                <StatusBadge status={authPostStatus} />
              </div>
              
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <Input
                    type="email"
                    placeholder="Enter user email..."
                    value={authPostEmail}
                    onChange={(e) => setAuthPostEmail(e.target.value)}
                    className="bg-slate-950/50 border-slate-700 text-slate-100"
                    data-testid="auth-post-email-input"
                  />
                </div>
                <div className="flex-1 min-w-[200px] relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter password to verify..."
                    value={authPostPassword}
                    onChange={(e) => setAuthPostPassword(e.target.value)}
                    className="bg-slate-950/50 border-slate-700 text-slate-100 pr-10"
                    data-testid="auth-post-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-200"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <Button
                  onClick={runAuthVerification}
                  disabled={authPostStatus === 'RUNNING' || !authPostEmail.trim() || !authPostPassword}
                  className="bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
                  data-testid="run-auth-post-btn"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${authPostStatus === 'RUNNING' ? 'animate-spin' : ''}`} />
                  Verify Password Against Stored Hash
                </Button>
              </div>
            </div>
            
            <div className="p-6">
              {/* Warnings */}
              <WarningBanner warnings={getAuthPostWarnings()} />
              
              {/* Key Signals */}
              {authPostResult && (
                <div className="mb-4">
                  <h4 className="text-xs text-slate-400 uppercase tracking-wider mb-2">Key Signals</h4>
                  <div className="flex flex-wrap gap-2">
                    <KeySignal 
                      label="can_authenticate" 
                      value={authPostResult.can_authenticate}
                      isOk={authPostResult.can_authenticate === true}
                    />
                    {authPostResult.reason && (
                      <KeySignal 
                        label="reason" 
                        value={authPostResult.reason}
                        isOk={authPostResult.can_authenticate === true}
                      />
                    )}
                  </div>
                </div>
              )}
              
              {/* Success indicator */}
              {authPostResult?.can_authenticate === true && (
                <div className="p-4 bg-green-500/10 border border-green-400/30 rounded-lg mb-4">
                  <div className="flex items-center gap-2 text-green-400 font-semibold">
                    <CheckCircle className="w-5 h-5" />
                    <span>Password verification PASSED</span>
                  </div>
                  <p className="text-sm text-green-300 mt-1">
                    The provided password matches the stored hash. Login should work.
                  </p>
                </div>
              )}
              
              {/* Error display */}
              {authPostError && (
                <div className="p-4 bg-red-500/10 border border-red-400/30 rounded-lg mb-4">
                  <div className="flex items-center gap-2 text-red-400 font-semibold mb-2">
                    <XCircle className="w-4 h-4" />
                    <span>Error</span>
                  </div>
                  <pre className="text-xs text-red-300 overflow-x-auto">
                    {typeof authPostError === 'string' ? authPostError : JSON.stringify(authPostError, null, 2)}
                  </pre>
                </div>
              )}
              
              {/* JSON Result */}
              {authPostResult && (
                <JSONViewer
                  data={authPostResult}
                  filename={`auth_consistency_post_${authPostEmail.replace('@', '_at_')}_${new Date().toISOString().replace(/[:.]/g, '-')}.json`}
                  timestamp={authPostTimestamp}
                  duration={authPostDuration}
                />
              )}
              
              {/* Empty state */}
              {!authPostResult && !authPostError && authPostStatus === 'IDLE' && (
                <p className="text-sm text-slate-500 italic">Enter email and password, then click &quot;Verify Password Against Stored Hash&quot;.</p>
              )}
            </div>
          </section>
          
          {/* =================================================================== */}
          {/* Panel 4: Database Reset (DANGER ZONE) */}
          {/* =================================================================== */}
          <section className="rounded-2xl bg-red-950/30 shadow-xl ring-1 ring-red-800 overflow-hidden">
            <div className="p-6 border-b border-red-800">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Trash2 className="w-6 h-6 text-red-400" />
                  <h2 className="text-lg font-bold text-red-300">4) Database Reset — DANGER ZONE</h2>
                  <code className="text-xs bg-red-900/50 px-2 py-1 rounded text-red-400">
                    POST /api/admin/diagnostics/reset-database
                  </code>
                </div>
                <StatusBadge status={resetStatus} />
              </div>
              
              <div className="p-4 bg-red-500/10 border border-red-400/30 rounded-lg mb-4">
                <div className="flex items-center gap-2 text-red-400 font-semibold mb-2">
                  <AlertTriangle className="w-5 h-5" />
                  <span>⚠️ DESTRUCTIVE OPERATION</span>
                </div>
                <ul className="text-sm text-red-300 space-y-1">
                  <li>• This will <strong>DELETE ALL DATA</strong> in the database</li>
                  <li>• All users, books, manuscripts, and content will be permanently removed</li>
                  <li>• Only the seeded admin accounts will remain after reset</li>
                  <li>• R2 audio storage is NOT affected (orphaned files will remain)</li>
                </ul>
              </div>
              
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <Input
                    type="text"
                    placeholder='Type "RESET_ALL_DATA" to confirm...'
                    value={resetConfirmation}
                    onChange={(e) => setResetConfirmation(e.target.value)}
                    className="bg-red-950/50 border-red-700 text-red-100 placeholder:text-red-400/50"
                    data-testid="reset-confirmation-input"
                  />
                </div>
                <Button
                  onClick={runDatabaseReset}
                  disabled={resetStatus === 'RUNNING' || resetConfirmation !== 'RESET_ALL_DATA'}
                  className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="run-reset-btn"
                >
                  <Trash2 className={`w-4 h-4 mr-2 ${resetStatus === 'RUNNING' ? 'animate-spin' : ''}`} />
                  {resetStatus === 'RUNNING' ? 'Resetting...' : 'Reset Database'}
                </Button>
              </div>
            </div>
            
            <div className="p-6">
              {/* Success indicator */}
              {resetResult?.status === 'complete' && (
                <div className="p-4 bg-green-500/10 border border-green-400/30 rounded-lg mb-4">
                  <div className="flex items-center gap-2 text-green-400 font-semibold">
                    <CheckCircle className="w-5 h-5" />
                    <span>Database Reset Complete</span>
                  </div>
                  <p className="text-sm text-green-300 mt-1">
                    {resetResult.summary}. Admin user present: {resetResult.admin_user_present ? 'Yes' : 'No'}
                  </p>
                </div>
              )}
              
              {/* Error display */}
              {resetError && (
                <div className="p-4 bg-red-500/10 border border-red-400/30 rounded-lg mb-4">
                  <div className="flex items-center gap-2 text-red-400 font-semibold mb-2">
                    <XCircle className="w-4 h-4" />
                    <span>Error</span>
                  </div>
                  <pre className="text-xs text-red-300 overflow-x-auto">
                    {typeof resetError === 'string' ? resetError : JSON.stringify(resetError, null, 2)}
                  </pre>
                </div>
              )}
              
              {/* JSON Result */}
              {resetResult && (
                <JSONViewer
                  data={resetResult}
                  filename={`database_reset_${new Date().toISOString().replace(/[:.]/g, '-')}.json`}
                  timestamp={resetTimestamp}
                  duration={resetDuration}
                />
              )}
              
              {/* Empty state */}
              {!resetResult && !resetError && resetStatus === 'IDLE' && (
                <p className="text-sm text-red-400/70 italic">Type the confirmation phrase and click Reset Database to proceed.</p>
              )}
            </div>
          </section>
          
          {/* =================================================================== */}
          {/* Security Notice Footer */}
          {/* =================================================================== */}
          <footer className="text-center py-6 border-t border-slate-800">
            <p className="text-xs text-slate-500">
              <strong className="text-slate-400">Admin-only.</strong> Do not share screenshots publicly.
            </p>
            <p className="text-xs text-slate-600 mt-1">
              This page is temporary and will be removed after production authentication is stabilized.
            </p>
          </footer>
        </main>
      </div>
      
      {/* Mobile Bottom Navigation */}
      <AdminMobileBottomNav />
    </div>
  );
};

export default AdminDiagnosticsPage;
